import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdminUser, ApiService, CreateAdminUserPayload } from '../../core/api.service';

@Component({
  selector: 'ch-admin-users',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './admin-users.component.html'
})
export class AdminUsersComponent implements OnInit {
  users: AdminUser[] = [];
  page = 1;
  pageSize = 10;
  total = 0;
  loading = false;
  saving = false;
  error = '';
  notice = '';
  form: CreateAdminUserPayload = {
    full_name: '',
    email: '',
    role: 'admin',
    status: 'active'
  };

  constructor(private readonly api: ApiService) {}

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const result = await this.api.adminUsers(this.page, this.pageSize);
      this.users = result.items;
      this.total = result.total;
    } catch {
      this.error = 'Could not load admin users.';
    } finally {
      this.loading = false;
    }
  }

  async create(): Promise<void> {
    if (!this.form.full_name.trim() || !this.form.email.trim() || this.saving) return;
    this.saving = true;
    this.error = '';
    this.notice = '';
    try {
      await this.api.createAdminUser({
        ...this.form,
        full_name: this.form.full_name.trim(),
        email: this.form.email.trim().toLowerCase()
      });
      this.form = { full_name: '', email: '', role: 'admin', status: 'active' };
      this.notice = 'Admin user created. A temporary password has been sent by email.';
      await this.load();
    } catch {
      this.error = 'Could not create admin user.';
    } finally {
      this.saving = false;
    }
  }

  async toggleStatus(user: AdminUser): Promise<void> {
    const nextStatus = user.status === 'active' ? 'inactive' : 'active';
    this.error = '';
    this.notice = '';
    try {
      await this.api.setAdminUserStatus(user.id, nextStatus);
      this.notice = `Admin user ${nextStatus === 'active' ? 'activated' : 'deactivated'}.`;
      await this.load();
    } catch {
      this.error = 'Could not update admin user status.';
    }
  }

  async resetPassword(user: AdminUser): Promise<void> {
    this.error = '';
    this.notice = '';
    try {
      await this.api.resetAdminUserPassword(user.id);
      this.notice = 'Temporary password sent by email. User will be prompted to change it on login.';
      await this.load();
    } catch {
      this.error = 'Could not reset admin user password.';
    }
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  async go(delta: number): Promise<void> {
    this.page = Math.min(this.totalPages, Math.max(1, this.page + delta));
    await this.load();
  }
}
